import './instrument.js' // Sentry init (async in prod)
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ChakraProvider } from '@chakra-ui/react'
import { system } from './theme/index.js'
import { App } from './App.js'
import './i18n/config.js'
import { disableTracking, flushTracking } from './lib/tracking.js'

// Inject Umami tracking script (skip if visitor is a panel user)
const umamiUrl = import.meta.env.VITE_UMAMI_URL
const umamiWebsiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID
if (umamiUrl && umamiWebsiteId) {
  const loadUmami = () => {
    // PII guard. The before-send hook is the PRIMARY defense: in Umami 2.x
    // data-exclude-hash only applies to SPA navigations, NOT to the landing
    // pageview — which is exactly /login#access_token=<JWT> after an OAuth
    // redirect (full tokens were ending up in umami.website_event.url_path).
    // Requires Umami >= 2.18 (before-send); exclude-hash alone is NOT enough
    // until v3. The hook also redacts bearer tokens carried in the path/query
    // (org invitations, ?invite=). MUST stay synchronous: pre-v3 trackers don't
    // await it, and a Promise payload would silently break all tracking.
    ;(window as unknown as Record<string, unknown>).__umamiStripHash =
      (_type: string, payload: { url?: string }) => {
        if (payload?.url) {
          const [path, query] = payload.url.split('#')[0].split('?')
          payload.url = path.replace(/\/(invitations)\/[^/]+/, '/$1/[redacted]')
            + (query ? '?' + query.replace(/((?:^|&)(invite|token|code)=)[^&]*/g, '$1[redacted]') : '')
        }
        return payload
      }
    const s = document.createElement('script')
    s.defer = true
    s.src = `${umamiUrl}/script.js`
    s.dataset.websiteId = umamiWebsiteId
    s.dataset.excludeHash = 'true'
    s.dataset.beforeSend = '__umamiStripHash'
    // Events fired before the script arrived are queued in lib/tracking.ts.
    s.onload = () => flushTracking()
    s.onerror = () => disableTracking()
    document.head.appendChild(s)
  }

  const apiUrl = import.meta.env.VITE_API_URL
  if (apiUrl) {
    fetch(`${apiUrl}/api/public/check-admin-ip`)
      .then((r) => r.json())
      .then((d) => {
        if (d.exclude) {
          localStorage.setItem('umami.disabled', '1')
          disableTracking()
        } else {
          localStorage.removeItem('umami.disabled')
          loadUmami()
        }
      })
      .catch(() => loadUmami()) // fail open: load analytics if check fails
  } else {
    loadUmami()
  }
} else {
  disableTracking()
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ChakraProvider value={system}>
      <App />
    </ChakraProvider>
  </StrictMode>,
)
